import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ChangeProfilePictureModalUI } from '@cube3/ui/src/Layout/AccountSettings/Modals/ChangeProfilePictureModalUI';
import { useModalActions } from '@cube3/state/src/redux/Hooks/useModalActions';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';
import { useDispatch } from 'react-redux';
import { useUploadImage } from '../hooks/useUploadImage';

interface ModalProps {
  modalContext: {
    /** Upload metadata config */
    config?: {
      targetResourceType: 'asset' | 'user' | 'share' | 'workspace';
      targetResourceId: string;
    };
    /** Modal title */
    title: string;
    shape?: 'circle' | 'square';
  };
}

type PreviewPicture = { name: string; src: string };

const ChangeProfilePictureModal: React.FC<ModalProps> = (props) => {
  const { title, config, shape } = props.modalContext;
  const { targetResourceType: resourceType, targetResourceId: resourceId } =
    config;
  const { previousModal, openModal } = useModalActions();
  const [targetImage, setTargetImage] = useState<PreviewPicture>();
  const [avatar, setAvatar] = useState<string>(undefined);
  /** for telling cropper when to crop image */
  const [shouldCrop, setShouldCrop] = useState(false);
  const [readingFile, setReadingFile] = useState(false);
  /** If it's updating the profile picture */
  const [processing, setProcessing] = useState(false);

  const dispatch = useDispatch();
  const { first: targetResource, retrieve } = useResource__ALPHA({
    resourceId,
    resourceType
  });

  /** handle drag and drop pictures in Dropzone */
  const handleOnDrop = useCallback((acceptedFile) => {
    const file = acceptedFile[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadstart = () => setReadingFile(true);
    reader.onload = (e) => {
      setTargetImage({
        name: file.name,
        src: e.target.result as string
      });
      setReadingFile(false);
    };
    reader.readAsDataURL(file);
  }, []);

  const {
    onUpload: uploadAvatar,
    status,
    error,
    setError
  } = useUploadImage(config);

  //upload avatar
  useEffect(() => {
    if (avatar) {
      setShouldCrop(false);
      uploadAvatar(avatar);
    }
  }, [avatar, setShouldCrop, uploadAvatar]);

  const lastModified = useRef(targetResource?.updated_at);

  /** polling user's profile_picture */
  useEffect(() => {
    if (status === 'succeeded') {
      if (
        (!targetResource['profile_picture'] &&
          !targetResource['display_image']) ||
        targetResource.updated_at === lastModified.current // If the `updated_at` column changes, we assume the `profile_picture` has also changed
      ) {
        setProcessing(true);
        const timer = setTimeout(() => retrieve(), 3000);
        return () => {
          window.clearTimeout(timer);
        };
      } else {
        setProcessing(false);
        previousModal();
      }
    }
  }, [dispatch, status, targetResource, avatar, previousModal]);

  useEffect(() => {
    if (error) {
      openModal('accountsettings_ProfilePicture_error', { error }, false);
      setError(undefined);
    }
  }, [error, openModal]);

  return (
    <ChangeProfilePictureModalUI
      shape={shape}
      title={title}
      onCloseEvent={previousModal}
      loading={status === 'uploading' || processing}
      image={targetImage}
      onDrop={handleOnDrop}
      onClickSave={() => setShouldCrop(true)}
      setAvatar={setAvatar}
      shouldCrop={shouldCrop}
      readingFile={readingFile}
    />
  );
};

export default ChangeProfilePictureModal;
